// Finance and Spending Overview (Tab 1)

import { useContext, useMemo } from 'react';

// context
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// components
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// charts
import { ChartDonut } from '../../../charts/ChartDonut/ChartDonut';

// utils
import {
	getContractAmountForCurrency,
	getContractCommitmentAmount,
} from '../Contracts/Contracts.utils';
import { categoryColors, fundFlowColors } from '../../../../utils/colors';
import {
	cardStyles,
	gradients,
	listItemStyles,
	progressBarStyles,
	shadows,
	spacing,
	statusIndicator,
	typography,
} from '../../../../utils/designSystem';

// mui
import { alpha, Box, Grid, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';

// icons
import {
	AccountBalance as IconBank,
	Dashboard as IconDashboard,
	Handshake as IconHandshake,
	Payments as IconPayments,
} from '@mui/icons-material';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { useTranslation } from 'react-i18next';

// Sub-Components

interface SkeletonValueProps {
	children: React.ReactNode;
	isLoading: boolean;
	width?: number | string;
}

const SkeletonValue = ({ children, isLoading, width = 80 }: SkeletonValueProps) => {
	if (isLoading) {
		return <Skeleton variant="text" width={width} sx={{ display: 'inline-block' }} />;
	}
	return <>{children}</>;
};

// Main Component

export const FinanceOverview = () => {
	const theme = useTheme();
	const { t } = useTranslation();

	// Context
	const { project } = useContext(ContextProject);
	const { accounts, activities, conditions, contracts, workPlans, loading, summary } =
		useContext(ContextSpendPlan);

	const {
		accounts: isLoadingAccounts,
		activities: isLoadingActivities,
		conditions: isLoadingConditions,
		contracts: isLoadingContracts,
		workPlans: isLoadingWorkPlans,
	} = loading;

	const isLoadingSummary =
		isLoadingWorkPlans || isLoadingContracts || isLoadingActivities || isLoadingConditions;

	// Get currency from designated accounts, fallback to project reporting currency
	const currency = useMemo(() => {
		// First try to get from designated accounts
		const daAccount = accounts.find((a) => a.type === 'DESIGNATED');
		if (daAccount?.currency) return daAccount.currency;

		// Fallback to project reporting currency
		const reportingCurrency = project?.currencies?.find((c) => c.type === 'REPORTING')?.currency;
		if (reportingCurrency) return reportingCurrency;

		// Final fallback
		return summary.currency || 'USD';
	}, [accounts, project, summary.currency]);

	// ==========================================================================
	// Computed Values
	// ==========================================================================

	const totalActivities = summary.activityCount;
	const totalContracts = summary.contractCount;

	// Contract totals are kept as per-currency maps rather than a single number.
	// Different contracts on a project can be denominated in different currencies
	// (the work plan's currency drives the lookup for each contract), so a
	// single rolled-up KPI would silently sum ETB into USD (or worse). Render
	// one row per currency; the single-headline KPI is only used when every
	// contract resolves to the same currency.
	const totalContractValueByCurrency = useMemo(() => {
		const byCurrency: Record<string, number> = {};
		contracts.forEach((contract) => {
			const workPlan = workPlans.find((e) => e.id === contract.workPlan);
			if (!workPlan) return;
			const amount = getContractAmountForCurrency(contract, workPlan.currency);
			if (amount == null) return;
			byCurrency[workPlan.currency] = (byCurrency[workPlan.currency] || 0) + amount;
		});
		return byCurrency;
	}, [contracts, workPlans]);

	const plannedTotal = useMemo(() => summary.totalAmount || 0, [summary.totalAmount]);
	const totalPaid = useMemo(() => summary.totalPaid || 0, [summary.totalPaid]);

	const committedTotalByCurrency = useMemo(() => {
		const workPlanById = new Map(workPlans.map((e) => [e.id, e] as const));
		const byCurrency: Record<string, number> = {};
		contracts.forEach((contract) => {
			const workPlan = workPlanById.get(contract.workPlan) || { currency };
			const amount = getContractCommitmentAmount(contract, workPlan);
			if (amount == null) return;
			byCurrency[workPlan.currency] = (byCurrency[workPlan.currency] || 0) + amount;
		});
		return byCurrency;
	}, [contracts, workPlans, currency]);

	// Single-number "Committed" total for the fund-flow chart is only safe when
	// every committed contract amount resolves to the display currency.
	// Otherwise the chart would conflate currencies, so we omit the slice.
	const committedTotalForDisplayCurrency = useMemo(() => {
		const currencies = Object.keys(committedTotalByCurrency);
		if (currencies.length === 0) return 0;
		if (currencies.length === 1 && currencies[0] === currency) {
			return committedTotalByCurrency[currency];
		}
		return null;
	}, [committedTotalByCurrency, currency]);

	const pendingPaymentsTotal = useMemo(() => {
		return conditions
			.filter((c) => c.status === 'VERIFIED' || c.status === 'SUBMITTED')
			.reduce((sum, c) => sum + (c.paymentAmount || 0), 0);
	}, [conditions]);

	const projectFinancing = useMemo(() => {
		const loans = project?.loans || [];
		const totalFinancing = loans.reduce((sum, loan) => sum + (loan.amount || 0), 0);
		return {
			label: loans.length > 0 ? t('spendPlan.financeOverview.worldBankFinancing') : t('spendPlan.financeOverview.projectFinancing'),
			amount: totalFinancing || summary.totalAmount,
		};
	}, [project, summary.totalAmount]);

	const resolveComponentName = (activity: {
		component?: number | { name?: string; componentName?: string } | null;
		componentName?: string;
	}) => {
		const fromObject =
			typeof activity.component === 'object'
				? activity.component?.name || activity.component?.componentName
				: undefined;
		const fromLoose = activity.componentName;
		const fromId = activity.component ? `Component ${activity.component}` : undefined;
			return fromObject || fromLoose || fromId || t('spendPlan.financeOverview.uncategorized');
	};

	const componentsBreakdown = useMemo(() => {
		const componentMap = new Map<string, number>();

		activities.forEach((activity) => {
			const name = resolveComponentName(activity);
			const current = componentMap.get(name) || 0;
			componentMap.set(name, current + (activity.amount || 0));
		});

		const colorKeys = Object.keys(categoryColors) as Array<keyof typeof categoryColors>;
		return Array.from(componentMap.entries())
			.map(([name, amount], index) => ({
				name,
				amount,
				color: categoryColors[colorKeys[index % colorKeys.length]],
			}))
			.sort((a, b) => b.amount - a.amount)
			.slice(0, 5);
	}, [activities]);

	const fundFlowData = useMemo(() => {
		const planned = plannedTotal;
		const redeemed = totalPaid;
		const verified = Math.max(redeemed + pendingPaymentsTotal, 0);

		const base: { name: string; value: number; color: string }[] = [
			{ name: t('spendPlan.financeOverview.planned'), value: planned, color: fundFlowColors.planned },
			{ name: t('spendPlan.financeOverview.verified'), value: verified, color: fundFlowColors.verified },
			{ name: t('spendPlan.financeOverview.redeemed'), value: redeemed, color: fundFlowColors.redeemed },
		];
		// Only surface "Committed" when every contract commitment resolves to a
		// single currency that matches the displayed one; otherwise the slice
		// would be a meaningless sum across mixed currencies.
		if (committedTotalForDisplayCurrency != null) {
			base.splice(1, 0, {
				name: t('spendPlan.financeOverview.committed'),
				value: committedTotalForDisplayCurrency,
				color: fundFlowColors.committed,
			});
		}
		return base;
	}, [plannedTotal, committedTotalForDisplayCurrency, pendingPaymentsTotal, totalPaid]);

	const fundFlowTotal = useMemo(() => {
		return fundFlowData.reduce((sum, item) => sum + item.value, 0);
	}, [fundFlowData]);

	const activitiesByComponent = useMemo(() => {
		const componentMap = new Map<
			string,
			{ activities: { name: string; amount: number }[]; total: number }
		>();

		activities.forEach((activity) => {
			const name = resolveComponentName(activity);

			if (!componentMap.has(name)) {
				componentMap.set(name, { activities: [], total: 0 });
			}

			const amount = activity.amount || 0;
			const target = componentMap.get(name);
			if (target) {
				target.activities.push({
					name: activity.name || `Activity #${activity.id}`,
					amount,
				});
				target.total += amount;
			}
		});

		return Array.from(componentMap.entries()).slice(0, 3);
	}, [activities]);

	const accountBalances = useMemo(() => {
		const daAccounts = accounts.filter((a) => a.type === 'DESIGNATED');
		const localAccounts = accounts.filter((a) => a.type !== 'DESIGNATED');

		const daBalance = daAccounts.reduce(
			(sum, a) => sum + (a.balances?.reduce((s, b) => s + b.balance, 0) || 0),
			0
		);
		const localBalance = localAccounts.reduce(
			(sum, a) => sum + (a.balances?.reduce((s, b) => s + b.balance, 0) || 0),
			0
		);

		// Get currency from first DA account, or first local account
		const daCurrency = daAccounts[0]?.currency;
		const localCurrency = localAccounts[0]?.currency;

		return { daBalance, localBalance, daCurrency, localCurrency };
	}, [accounts]);

	// ==========================================================================
	// Render
	// ==========================================================================

	return (
		<Box>
			{/* Header - Full width, no padding */}
			<HeaderListingPage
				icon={IconDashboard}
				title={t('spendPlan.planningSummary')}
				subtitle={t('spendPlan.planningSummarySubtitle')}
				variant="contract"
			/>

			{/* Content with padding */}
			<Box sx={{ pb: spacing.xl, px: { xs: spacing.sm, sm: spacing.md, md: spacing.lg }, pt: 2 }}>
				<Paper
					elevation={0}
					sx={{
						...cardStyles.featured(theme),
						mb: spacing.xl,
					}}>
					{/* Top accent gradient bar */}
					<Box
						sx={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							height: 4,
							background: gradients.heroAccent(theme),
						}}
					/>

					<Grid container spacing={{ xs: spacing.sm, md: spacing.md }} alignItems="center">
						{/* Total No. of Activities */}
						<Grid item xs={6} sm={4} md={4}>
							<Box
								sx={{
									p: { xs: spacing.md, sm: spacing.lg },
									textAlign: { xs: 'center', md: 'left' },
								}}>
								<Typography
									sx={{
										...typography.labelSmall,
										color: 'text.secondary',
										mb: 1.5,
										fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem' },
									}}>
									{t('spendPlan.financeOverview.totalActivities')}
								</Typography>
								<Typography
									sx={{
										...typography.displayMedium,
										color: 'text.primary',
										fontSize: { xs: '2rem', sm: '2.5rem', md: '3.25rem' },
									}}>
									<SkeletonValue isLoading={isLoadingActivities} width={60}>
										{totalActivities}
									</SkeletonValue>
								</Typography>
							</Box>
						</Grid>

						{/* Total No. of Contracts */}
						<Grid item xs={6} sm={4} md={4}>
							<Box
								sx={{
									p: { xs: spacing.md, sm: spacing.lg },
									textAlign: { xs: 'center', md: 'left' },
								}}>
								<Typography
									sx={{
										...typography.labelSmall,
										color: 'text.secondary',
										mb: 1.5,
										fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem' },
									}}>
									{t('spendPlan.financeOverview.totalContracts')}
								</Typography>
								<Typography
									sx={{
										...typography.displayMedium,
										color: 'text.primary',
										fontSize: { xs: '2rem', sm: '2.5rem', md: '3.25rem' },
									}}>
									<SkeletonValue isLoading={isLoadingContracts} width={60}>
										{totalContracts}
									</SkeletonValue>
								</Typography>
							</Box>
						</Grid>

						{/* Total Contract Value (Summary) */}
						<Grid item xs={12} sm={4} md={4}>
							<Box
								sx={{
									p: { xs: spacing.md, sm: spacing.lg },
									textAlign: { xs: 'center', md: 'left' },
								}}>
								<Typography
									sx={{
										...typography.labelSmall,
										color: 'text.secondary',
										mb: 1.5,
										fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem' },
									}}>
									{t('spendPlan.financeOverview.totalContractValue')}
								</Typography>
								<SkeletonValue isLoading={isLoadingSummary} width={180}>
									{(() => {
										const entries = Object.entries(totalContractValueByCurrency);
										if (entries.length === 0) {
											return (
												<Typography
													sx={{
														...typography.displaySmall,
														color: 'text.primary',
														fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.5rem' },
													}}>
													—
												</Typography>
											);
										}
										return entries.map(([cur, value]) => (
											<Typography
												key={cur}
												sx={{
													...typography.displaySmall,
													color: 'text.primary',
													fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.5rem' },
												}}>
												{formatCurrency({ currency: cur, value })}
											</Typography>
										));
									})()}
								</SkeletonValue>
							</Box>
						</Grid>
					</Grid>
				</Paper>

				{/* ================================================================
			    Main Content
			    ================================================================ */}
				<Grid container spacing={{ xs: spacing.md, md: spacing.lg }}>
					{/* ============================================================
				    Left Column
				    ============================================================ */}
					<Grid item xs={12} lg={6}>
						{/* Project Financing */}
						<Typography
							sx={{
								...typography.h2,
								color: 'text.primary',
								mb: spacing.md,
								fontSize: { xs: '1.5rem', sm: '1.625rem', md: '1.875rem' },
							}}>
							{t('spendPlan.financeOverview.projectFinancing')}
						</Typography>
						<Paper
							elevation={0}
							sx={{
								...cardStyles.base(theme),
								p: { xs: spacing.md, sm: spacing.lg },
								mb: spacing.xl,
								position: 'relative',
								overflow: 'hidden',
							}}>
							{/* Background glow */}
							<Box
								sx={{
									position: 'absolute',
									top: -60,
									right: -60,
									width: { xs: 120, md: 180 },
									height: { xs: 120, md: 180 },
									borderRadius: '50%',
									background: gradients.radialGlow(theme.palette.primary.main, 0.08),
								}}
							/>
							<Stack
								direction="row"
								spacing={{ xs: spacing.md, sm: spacing.lg }}
								alignItems="center"
								sx={{ position: 'relative' }}>
								<Box
									sx={{
										alignItems: 'center',
										background: gradients.primary(theme),
										borderRadius: '50%',
										boxShadow: shadows.icon(theme.palette.primary.main),
										display: 'flex',
										height: { xs: 44, sm: 56 },
										justifyContent: 'center',
										width: { xs: 44, sm: 56 },
										flexShrink: 0,
									}}>
									<IconHandshake sx={{ color: '#fff', fontSize: { xs: 20, sm: 26 } }} />
								</Box>
								<Box sx={{ minWidth: 0 }}>
									<Typography
										sx={{
											fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
											fontWeight: 700,
											color: 'text.primary',
											mb: 0.5,
											wordBreak: 'break-word',
										}}>
										{projectFinancing.label}
									</Typography>
									<Typography
										sx={{
											fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
											fontWeight: 400,
											color: 'text.primary',
										}}>
										<SkeletonValue isLoading={isLoadingWorkPlans} width={160}>
											{formatCurrency({ currency, value: projectFinancing.amount })}
										</SkeletonValue>
									</Typography>
								</Box>
							</Stack>
						</Paper>

						{/* Components */}
						<Typography
							sx={{
								...typography.h2,
								color: 'text.primary',
								mb: spacing.md,
								fontSize: { xs: '1.5rem', sm: '1.625rem', md: '1.875rem' },
							}}>
							{t('spendPlan.financeOverview.components')}
						</Typography>
						<Box sx={{ mb: spacing.xl }}>
							{componentsBreakdown.length > 0 ? (
								componentsBreakdown.map((component, index) => (
									<Box key={component.name} sx={listItemStyles.rowWithLeader(theme)}>
										<Stack
											direction="row"
											alignItems="center"
											spacing={spacing.md}
											sx={{ flexShrink: 0 }}>
											<Box sx={statusIndicator.dot(component.color, 12)} />
											<Typography sx={{ ...typography.bodyLarge, color: 'text.primary' }}>
												{component.name}
											</Typography>
										</Stack>
										<Box className="leader" />
										<Typography
											sx={{ ...typography.valueSmall, color: 'text.primary', flexShrink: 0 }}>
											<SkeletonValue isLoading={isLoadingActivities} width={120}>
												{formatCurrency({ currency, value: component.amount })}
											</SkeletonValue>
										</Typography>
									</Box>
								))
							) : (
								<Box sx={{ p: spacing.xl, textAlign: 'center' }}>
									<Typography sx={{ ...typography.body, color: 'text.secondary' }}>
									{t('spendPlan.financeOverview.noComponents')}
									</Typography>
								</Box>
							)}
						</Box>

						{/* Activities, Contracts, and Their Value */}
						<Typography
							sx={{
								...typography.h2,
								color: 'text.primary',
								mb: spacing.md,
								fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.875rem' },
							}}>
							{t('spendPlan.financeOverview.activitiesContractsValue')}
						</Typography>
						<Box>
							{activitiesByComponent.length > 0 ? (
								activitiesByComponent.map(([componentName, data], compIndex) => (
									<Box key={componentName}>
										{/* Component Section Header with total */}
										<Box sx={listItemStyles.rowWithLeader(theme)}>
											<Typography
												sx={{
													...typography.labelSmall,
													color: 'text.secondary',
													textTransform: 'uppercase',
													letterSpacing: '0.05em',
													flexShrink: 0,
												}}>
												{componentName}
											</Typography>
											<Box className="leader" />
											<Typography
												sx={{ ...typography.valueSmall, color: 'text.secondary', flexShrink: 0 }}>
												{formatCurrency({ currency, value: data.total })}
											</Typography>
										</Box>
										{/* Activities List */}
										{data.activities.slice(0, 4).map((activity, actIndex) => (
											<Box
												key={activity.name}
												sx={{
													...listItemStyles.rowWithLeader(theme),
													pl: spacing.md,
												}}>
												<Typography
													sx={{ ...typography.bodyLarge, color: 'text.primary', flexShrink: 0 }}>
													{activity.name}
												</Typography>
												<Box className="leader" />
												<Typography
													sx={{ ...typography.valueSmall, color: 'text.primary', flexShrink: 0 }}>
													{formatCurrency({ currency, value: activity.amount })}
												</Typography>
											</Box>
										))}
									</Box>
								))
							) : (
								<Box sx={{ p: spacing.xl, textAlign: 'center' }}>
									<Typography sx={{ ...typography.body, color: 'text.secondary' }}>
									{t('spendPlan.financeOverview.noActivities')}
									</Typography>
								</Box>
							)}
						</Box>
					</Grid>

					{/* ============================================================
				    Right Column
				    ============================================================ */}
					<Grid item xs={12} lg={6}>
						{/* Fund Flow Allocation */}
						<Typography
							sx={{
								...typography.h2,
								color: 'text.primary',
								mb: spacing.md,
								fontSize: { xs: '1.5rem', sm: '1.625rem', md: '1.875rem' },
							}}>
							{t('spendPlan.financeOverview.fundFlowAllocation')}
						</Typography>
						<Box
							sx={{
								mb: spacing.lg,
								position: 'relative',
							}}>
							<Grid
								container
								columnSpacing={{ xs: spacing.md, md: spacing.lg }}
								rowSpacing={spacing.md}
								alignItems="center"
								sx={{ position: 'relative' }}>
								{/* Donut Chart - show first on mobile for visual impact */}
								<Grid item xs={12} sm={6} md={5} sx={{ p: 0, order: { xs: 0, md: 1 } }}>
									<Box
										sx={{
											display: 'flex',
											justifyContent: { xs: 'center', md: 'flex-end' },
											alignItems: 'center',
											width: '100%',
											maxWidth: { xs: 220, sm: 240, md: 280, lg: 300 },
											mx: 'auto',
											mr: { md: 0 },
										}}>
										<ChartDonut data={fundFlowData} size="auto" innerRadius={55} outerRadius={92} />
									</Box>
								</Grid>

								{/* Legend with progress bars */}
								<Grid item xs={12} sm={6} md={7} sx={{ pl: 0, order: { xs: 1, md: 0 } }}>
									<Stack spacing={{ xs: spacing.sm, md: spacing.md }}>
										{fundFlowData.map((item) => {
											const percentage = fundFlowTotal > 0 ? (item.value / fundFlowTotal) * 100 : 0;
											return (
												<Box key={item.name}>
													<Stack
														direction="row"
														alignItems="center"
														justifyContent="space-between"
														flexWrap="wrap"
														sx={{ mb: 1, gap: { xs: 0.5, sm: 1 } }}>
														<Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
															<Box
																sx={{
																	bgcolor: item.color,
																	borderRadius: 1,
																	height: { xs: 10, sm: 12, md: 14 },
																	width: { xs: 10, sm: 12, md: 14 },
																	boxShadow: `0 2px 4px ${alpha(item.color, 0.4)}`,
																	flexShrink: 0,
																}}
															/>
															<Typography
																sx={{
																	...typography.bodyLarge,
																	color: 'text.primary',
																	fontSize: {
																		xs: '0.875rem',
																		sm: '1rem',
																		md: '1.25rem',
																		lg: '1.5rem',
																	},
																}}>
																{item.name}
															</Typography>
														</Stack>
														<Typography
															sx={{
																...typography.valueSmall,
																color: 'text.primary',
																fontSize: {
																	xs: '0.875rem',
																	sm: '1rem',
																	md: '1.125rem',
																	lg: '1.375rem',
																},
															}}>
															<SkeletonValue isLoading={isLoadingSummary} width={100}>
																{formatCurrency({ currency, value: item.value })}
															</SkeletonValue>
														</Typography>
													</Stack>
													{/* Progress bar */}
													<Box sx={progressBarStyles.track(item.color)}>
														<Box sx={progressBarStyles.fill(item.color, percentage)} />
													</Box>
												</Box>
											);
										})}
									</Stack>
								</Grid>
							</Grid>
						</Box>

						{/* Account Balance Cards */}
						<Stack spacing={{ xs: spacing.sm, md: spacing.md }}>
							{/* Amount in DA */}
							<Paper
								elevation={0}
								sx={{
									...cardStyles.base(theme),
									p: { xs: spacing.md, sm: spacing.lg },
									position: 'relative',
									overflow: 'hidden',
								}}>
								<Box
									sx={{
										position: 'absolute',
										top: -40,
										right: -40,
										width: { xs: 100, md: 140 },
										height: { xs: 100, md: 140 },
										borderRadius: '50%',
										background: gradients.radialGlow('#0288d1', 0.1),
									}}
								/>
								<Stack
									direction="row"
									spacing={{ xs: spacing.md, sm: spacing.lg }}
									alignItems="center"
									sx={{ position: 'relative' }}>
									<Box
										sx={{
											alignItems: 'center',
											background: '#1F6BCE',
											borderRadius: '50%',
											boxShadow: shadows.icon('#1F6BCE'),
											display: 'flex',
											height: { xs: 44, sm: 52 },
											justifyContent: 'center',
											width: { xs: 44, sm: 52 },
											flexShrink: 0,
										}}>
										<IconBank sx={{ color: '#fff', fontSize: { xs: 20, sm: 24 } }} />
									</Box>
									<Box sx={{ minWidth: 0 }}>
										<Typography
											sx={{
												fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
												fontWeight: 700,
												color: 'text.primary',
												mb: 0.5,
											}}>
											{t('spendPlan.financeOverview.amountInDA')}
										</Typography>
										<Typography
											sx={{
												fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
												fontWeight: 400,
												color: 'text.primary',
											}}>
											<SkeletonValue isLoading={isLoadingAccounts} width={160}>
												{formatCurrency({
													currency: accountBalances.daCurrency || currency,
													value: accountBalances.daBalance,
												})}
											</SkeletonValue>
										</Typography>
									</Box>
								</Stack>
							</Paper>

							{/* Amount in Local Accounts */}
							<Paper
								elevation={0}
								sx={{
									...cardStyles.base(theme),
									p: { xs: spacing.md, sm: spacing.lg },
									position: 'relative',
									overflow: 'hidden',
								}}>
								<Box
									sx={{
										position: 'absolute',
										top: -40,
										right: -40,
										width: { xs: 100, md: 140 },
										height: { xs: 100, md: 140 },
										borderRadius: '50%',
										background: gradients.radialGlow('#2e7d32', 0.1),
									}}
								/>
								<Stack
									direction="row"
									spacing={{ xs: spacing.md, sm: spacing.lg }}
									alignItems="center"
									sx={{ position: 'relative' }}>
									<Box
										sx={{
											alignItems: 'center',
											background: '#D97232',
											borderRadius: '50%',
											boxShadow: shadows.icon('#D97232'),
											display: 'flex',
											height: { xs: 44, sm: 52 },
											justifyContent: 'center',
											width: { xs: 44, sm: 52 },
											flexShrink: 0,
										}}>
										<IconPayments sx={{ color: '#fff', fontSize: { xs: 20, sm: 24 } }} />
									</Box>
									<Box sx={{ minWidth: 0 }}>
										<Typography
											sx={{
												fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
												fontWeight: 700,
												color: 'text.primary',
												mb: 0.5,
												wordBreak: 'break-word',
											}}>
										{t('spendPlan.financeOverview.amountInLocalAccounts')}
										</Typography>
										<Typography
											sx={{
												fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
												fontWeight: 400,
												color: 'text.primary',
											}}>
											<SkeletonValue isLoading={isLoadingAccounts} width={160}>
												{formatCurrency({
													currency: accountBalances.localCurrency || currency,
													value: accountBalances.localBalance,
												})}
											</SkeletonValue>
										</Typography>
									</Box>
								</Stack>
							</Paper>
						</Stack>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};
